import {
	AfterContentInit,
	Component,
	forwardRef,
	Injectable, Injector,
	Input, OnChanges,
	OnInit, Optional,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import * as moment from 'moment-timezone';
import {
	NgbDate, NgbDateParserFormatter,
	NgbDatepickerI18n,
	NgbDateStruct,
	NgbInputDatepicker
} from '@ng-bootstrap/ng-bootstrap';
import {
	ControlContainer,
	ControlValueAccessor,
	NG_VALUE_ACCESSOR,
	NgControl
} from '@angular/forms';


@Injectable()
export class I18n {
	language = 'ru';
	I18N_VALUES = {
		'ru': {
			weekdays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
			months: ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'],
		}
	};
}


@Injectable()
export class CustomDatepickerI18n extends NgbDatepickerI18n {
	constructor(private _i18n: I18n) {
		super();
	}

	getWeekdayShortName(weekday: number): string {
		return this._i18n.I18N_VALUES[this._i18n.language].weekdays[weekday - 1];
	}
	
	getMonthShortName(month: number): string {
		return this._i18n.I18N_VALUES[this._i18n.language].months[month - 1];
	}
	
	getMonthFullName(month: number): string {
		return this.getMonthShortName(month);
	}
	
	getDayAriaLabel(date: NgbDateStruct): string {
		return `${date.year}-${date.month}-${date.day}`;
	}
}


@Injectable()
export class CustomParserFormatter extends NgbDateParserFormatter {
	public _format = 'YYYY-MM-DD';
	
	parse(value: string): NgbDateStruct {
		const dtIn = moment(value, this._format, true);
		
		if (dtIn.isValid()) {
			return dt2model(dtIn);
		}
		
		return null;
	}
	
	format(date: NgbDateStruct): string {
		if (date == null) {
			return '';
		}
		
		const d = Object.assign({}, date);
		d.month = (d.month === 0 ? 11 : d.month - 1);
		
		return moment(d).format(this._format);
	}
}

const dt2model = (dateM): IModel => {
	return {
		year  : dateM.year(),
		month : dateM.month() + 1,
		day   : dateM.date()
	};
};

interface IModel {
	year: number;
	month: number;
	day: number;
}

@Component({
	// tslint:disable-next-line:component-selector
	selector: 'datepicker',
	template: `
		<input
			type="text"
			autocomplete="off"
			class="form-control"
			[placeholder]="format"
			name="dp"
			[(ngModel)]="model"
			ngbDatepicker
			#datepicker="ngbDatepicker"
			(click)="onInputClick()"
			(input)="onInput($event)"
			(dateSelect)="onDateSelection($event)"
			[disabled]="disabled"
		>
		
		<span
			class="fa fa-calendar-alt"
			aria-hidden="true"
			(click)="onClick()"
		></span>
	`,
	styles: [`
		:host >>> .fa-calendar-alt {
			width: 15px;
			margin: -27px 10px;
			float: right;
		}
		
		:host >>> input::placeholder { 
			font-size: 0.9rem; 
		}
	`],
	providers: [
		I18n,
		{provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n},
		{provide: NgbDateParserFormatter, useClass: CustomParserFormatter},
		{provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DatepickerComponent), multi: true}
	]
})
export class DatepickerComponent implements ControlValueAccessor, OnInit, OnChanges, AfterContentInit {
	
	// Объект модели датапикера - внутрений для ng-bootsrap datapicker формат
	@Input() model: IModel;
	
	@Input() format       = 'YYYY-MM-DD';
	@Input() disabled     = false;
	@Input() disabledPast = false;
	
	@Input() minDate: NgbDateStruct;
	@Input() maxDate: NgbDateStruct = {
		year  : 3000,
		month : 12,
		day   : 31
	};
	
	@ViewChild('datepicker', { static: true })
	private datepicker: NgbInputDatepicker;
	
	private formControl = null;
	private ngControl: NgControl;
	
	constructor(
		@Optional()
		private injector: Injector,
		@Optional()
		private controlContainer: ControlContainer,
	) {
	
	}
	
	ngOnInit() {
		this.datepicker['_parserFormatter']._format = this.format;
		
		if (this.disabledPast) {
			const dt = moment();
			this.minDate = {year: dt.year(), month: dt.month() + 1, day: dt.date()};
		}
		
		this.datepicker.minDate = this.minDate;
		this.datepicker.maxDate = this.maxDate;
		
		const dtIn = moment(this.model, this.format, true);
		
		if (dtIn.isValid()) {
			this.model = dt2model(dtIn);
		}
	}
	
	ngAfterContentInit() {
		this.ngControl = this.injector.get(NgControl);
		
		if ( ! this.ngControl.name) {
			return;
		}
		
		this.formControl = this.controlContainer.control.get(this.ngControl.name);
		if (this.disabled) {
			this.formControl.disable();
		} else {
			this.formControl.enable();
		}
	}
	
	ngOnChanges(changes: SimpleChanges) {
		if (changes.disabled && this.formControl) {
			if (changes.disabled.currentValue) {
				this.formControl.disable();
			} else {
				this.formControl.enable();
			}
		}
	}
	
	onDateSelection(date: NgbDate) {
		date.month = (date.month === 0 ? 11 : date.month - 1);
		
		this.writeValue(moment(date).format(this.format));
	}
	
	onInput(event) {
		this.writeValue(event.target.value);
	}
	
	onClick() {
		if (!this.disabled) {
			this.datepicker.toggle();
		}
	}
	
	onInputClick() {
		
		// Для статистики показываем календарик ещё и по клику на инпут, для саппорта только по иконке 
		if (this.formControl != null) {
			return;
		}
	
		if (!this.disabled) {
			this.datepicker.toggle();
		}
	}
	
	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
	}
	
	writeValue(value) {
		if (value) {
			const dtIn = moment(value, [this.format, 'YYYY-MM-DDTHH:mm:ss.SSSZ'], true);
			
			if (dtIn.isValid()) {
				this.model = dt2model(dtIn);
			}
			
			this.onChange(value);
		}
	}
	
	onChange: any = () => {};
	
	onTouched: any = () => {};
	
	registerOnChange(fn) {
		this.onChange = fn;
	}
	
	registerOnTouched(fn) {
		this.onTouched = fn;
	}
}
